// Ported from dev-scrips-pm-react src/shared/lib/time-format.ts (PROD-3313, Andrew's review: times were 24-hour with
// no way to change them). Replaces the PA's 24-hour-only `clock-time.ts`.
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Time format — one formatter for every clock time the app shows.
 *
 * 12-hour ("9:00a", "1:45p", the app's compact form) is the default, as on the Practice app (PM team decision, 19 Oct,
 * reverting the 13 Oct 24-hour default); 24-hour ("09:00", "13:45") is a display preference in Account Settings ›
 * Account details › Display.
 *
 * The preference is the viewer's, kept in this browser under the same key as the Practice app
 * (`scrips.time-format`): there is no backend field for it yet. It is a per-browser display preference, not server
 * data, so it is NOT one of the user-scoped stores that reset on an auth change. `persist` writes only through
 * `setFormat` / `setState`, never on hydration, so a stored value exists only for a viewer who picked one. Plain
 * functions read it at call time (`getTimeFormat`), so mappers outside React follow it too; a component that must
 * re-render on a change subscribes with `useTimeFormat`.
 */
export type TimeFormat = '24h' | '12h';

interface TimeFormatState {
  format: TimeFormat;
  setFormat: (format: TimeFormat) => void;
}

export const useTimeFormatStore = create<TimeFormatState>()(
  persist(
    (set) => ({
      format: '12h',
      setFormat: (format) => set({ format }),
    }),
    { name: 'scrips.time-format' },
  ),
);

/** The current format, for code outside React. */
export function getTimeFormat(): TimeFormat {
  return useTimeFormatStore.getState().format;
}

/** The current format, subscribed: the component re-renders when it changes. */
export function useTimeFormat(): TimeFormat {
  return useTimeFormatStore((s) => s.format);
}

const pad = (n: number) => String(n).padStart(2, '0');

/** "13:45" (24h) or "1:45p" (12h). */
export function formatClockParts(hours: number, minutes: number, format: TimeFormat = getTimeFormat()): string {
  if (format === '24h') return `${pad(hours)}:${pad(minutes)}`;
  const h = hours % 12 === 0 ? 12 : hours % 12;
  return `${h}:${pad(minutes)}${hours >= 12 ? 'p' : 'a'}`;
}

/** A Date's wall-clock time, in the current format. */
export function formatClockTime(d: Date, format: TimeFormat = getTimeFormat()): string {
  return formatClockParts(d.getHours(), d.getMinutes(), format);
}

/** An hour mark for a time gutter: "13:00" (24h) or "1p" (12h). */
export function formatHourMark(hour: number, format: TimeFormat = getTimeFormat()): string {
  if (format === '24h') return `${pad(hour)}:00`;
  if (hour === 0) return '12a';
  if (hour === 12) return '12p';
  return hour > 12 ? `${hour - 12}p` : `${hour}a`;
}

/**
 * A clock time the server sends as TEXT ("09:00 AM", "9:00 pm", "14:30"), re-shown in the current format.
 * Display only: text that isn't a clock time comes back unchanged.
 */
export function formatClockText(text: string, format: TimeFormat = getTimeFormat()): string {
  const m = /^\s*(\d{1,2}):(\d{2})(?::\d{2})?\s*([AaPp])?\.?\s*[Mm]?\.?\s*$/.exec(text);
  if (!m) return text;
  let hours = Number(m[1]);
  const minutes = Number(m[2]);
  const meridian = m[3]?.toLowerCase();
  if (meridian === 'a' && hours === 12) hours = 0;
  else if (meridian === 'p' && hours < 12) hours += 12;
  if (hours > 23 || minutes > 59) return text;
  return formatClockParts(hours, minutes, format);
}
